import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import Modal from 'react-modal';
import { TFunction, useTranslation } from 'react-i18next';
import Image from 'next/image';
import { useMedia } from 'react-use';

import Close from 'public/static/images/onboarding/close.svg';
import useEvents from 'lib/hooks/useEvents';
import tapEvent from 'lib/Tracking/Onboarding/TapEvent';
import viewEvent from 'lib/Tracking/Onboarding/ViewEvent';
import { SourcePages } from 'lib/EventSchema';
import { screen } from 'components/styles/variables';
import { OnboardingFilterProvider, PartialSrpFilterValues } from 'contexts/OnboardingFilterContext';
import { SrpFilterApiParams } from 'lib/srpFilterTransformers';
import { srpFilterInitialValues, useSrpFilters } from 'lib/hooks/useSrpFilters';

import _ from 'lodash';
import Location from '../Location';
import Budget from '../Budget';
import Welcome from '../Welcome';
import styles, { onboardingModalStyles } from './styles';
import CloseModal from '../CloseModal';
import ProgressBar from '../ProgressBar';
import Condition from '../Condition';
import Year from '../Year';
import Mileage from '../Mileage';
import Loading from '../Loading';
import FuelType from '../FuelType';
import BodyStyle from '../BodyStyle';

export interface OnboardingModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  shouldShowCloseButton?: boolean;
  searchVehicles: (sortBy?: string) => Promise<void>;
  isPending: boolean;
}

export interface OnboardingComponentProps {
  t: TFunction<'translation', undefined>;
  onNextStep: (eventContext?: any) => void;
  onPreviousStep: () => void;
  onGoToStep: (step: number) => void;
  onPerformSearch?: (data: PartialSrpFilterValues) => void;
}

interface ScreenMapItem {
  component: React.ReactElement;
  sourcePage: SourcePages;
}

const ScreensMap: ScreenMapItem[] = [
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Welcome />, sourcePage: SourcePages.OnboardingWelcome },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Location />, sourcePage: SourcePages.OnboardingLocation },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Budget />, sourcePage: SourcePages.OnboardingPriceRange },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Condition />, sourcePage: SourcePages.OnboardingCondition },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <BodyStyle />, sourcePage: SourcePages.OnboardingBodyStyle },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Year />, sourcePage: SourcePages.OnboardingYear },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <FuelType />, sourcePage: SourcePages.OnboardingFuelType },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  { component: <Mileage />, sourcePage: SourcePages.OnboardingMileage },
];

export default function OnboardingModal({
  isModalOpen,
  setIsModalOpen,
  shouldShowCloseButton = true,
  searchVehicles,
  isPending,
}: OnboardingModalProps): JSX.Element {
  const logger = useEvents();
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const shouldShowProgressBar = currentStep >= 1 && currentStep <= 7;
  const isDesktop = useMedia(`(min-width: ${screen.smMin})`);
  const { setValues } = useSrpFilters();

  const onLeave = useCallback(() => {
    setIsCloseModalOpen(false);
    setIsModalOpen(false);
    logger.log(tapEvent({ action: 'leave', sourcePage: ScreensMap[currentStep].sourcePage }));
    setCurrentStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsModalOpen, currentStep]);

  // we cannot retrieve onboaridng data on this page because the context provider does not surround it

  const onCancel = useCallback(() => {
    setIsCloseModalOpen(false);
    logger.log(tapEvent({ action: 'cancel', sourcePage: ScreensMap[currentStep].sourcePage }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const onNextStep = useCallback(
    (eventContext?: any) => {
      setCurrentStep((step) => {
        logger.log(
          tapEvent({
            action: 'next',
            sourcePage: ScreensMap[currentStep].sourcePage,
            eventContext,
          })
        );
        if (step + 1 < 8) return Number(step + 1);
        return step;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep]
  );

  const onPerformSearch = (params: SrpFilterApiParams): void => {
    /*
      Onboarding filter state is set in Redux prior to invoking searchVehicles
      because searchVehicles does not take filter state as a parameter. It is
      passed in from SRP via the useSrpModel hook and it performs a search when
      invoked using the current Redux state. There may be room for a potential refactor here

      Onboarding filter state must be passed in to this function since the Onboarding modal
      does not have access to the OnboardingFilter Context - we would have to wrap SRP and SEO SRP
      in the OnboardingFilterProvider or look into refactoring our modal to change this
    */
    setValues(_.merge({}, srpFilterInitialValues, params));
    searchVehicles().then(() => onLeave());
  };

  const onPreviousStep = useCallback(
    () =>
      setCurrentStep((step) => {
        if (step - 1 > 0) return Number(step - 1);
        return step;
      }),
    []
  );

  const onGoToStep = useCallback((step: number): void => setCurrentStep(step), []);

  return (
    <>
      <OnboardingFilterProvider>
        <Modal
          isOpen={isModalOpen}
          contentLabel="Onboarding Modal"
          style={onboardingModalStyles(isDesktop)}
          htmlOpenClassName="html-modal-open"
        >
          {shouldShowProgressBar && <ProgressBar currentStep={currentStep} totalSteps={7} />}
          {shouldShowCloseButton && (
            <div className="close-button">
              <Image
                alt="close-icon"
                height={20}
                width={20}
                src={Close}
                style={{ marginTop: '16px', marginRight: '16px' }}
                onClick={(): void => {
                  setIsCloseModalOpen(true);

                  logger.log(viewEvent({ sourcePage: SourcePages.OnboardingExit }));
                  logger.log(
                    tapEvent({ action: 'close', sourcePage: ScreensMap[currentStep].sourcePage })
                  );
                }}
              />
            </div>
          )}
          <div className="modal-content">
            {isPending ? (
              <Loading />
            ) : (
              React.cloneElement(ScreensMap[currentStep].component, {
                onNextStep,
                onPreviousStep,
                onGoToStep,
                onPerformSearch,
                t,
              })
            )}
          </div>
        </Modal>
        <CloseModal isModalOpen={isCloseModalOpen} onCancel={onCancel} onLeave={onLeave} />
        <style jsx>{styles}</style>
      </OnboardingFilterProvider>
    </>
  );
}
